import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RiShoppingCartLine, RiFileList3Line, RiArrowRightSLine } from '@remixicon/react';
import { useAppStore } from '@store/appStore';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@components/ui/Drawer';
import Button from '@components/ui/Button';
import ShortlistedCard from './ShortlistedCard';
import { ROUTES } from '@/constants';
/**
 * Shortlist Component
 * Interactive drawer showing shortlisted bids with a floating action button
 *
 * @param {Object} props
 * @param {Function} props.onViewBid - Callback when viewing a bid
 * @param {boolean} props.useRelativePosition - Use relative positioning instead of fixed
 * @param {boolean} props.isMobile - Use mobile floating button style
 */
const Shortlist = ({ onViewBid, useRelativePosition = false, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { setSelectedTripGroup, clearShortlist, bids = [] } = useAppStore();
  const navigate = useNavigate();

  // Derive shortlisted bids from bidOptions
  const shortlistedBids = bids.filter(bid => {
    // Check if bid_options exists and is not empty
    if (!bid.bid_options || !Array.isArray(bid.bid_options) || bid.bid_options.length === 0) {
      return false;
    }

    // Check if any bid_option has shortlisted === true
    return bid.bid_options.some(option => option.shortlisted === true);
  });

  // Group shortlisted bids by trip_number
  const groupedByTrip = shortlistedBids.reduce((acc, bid) => {
    const tripNumber = bid.trip_number;
    if (!acc[tripNumber]) {
      acc[tripNumber] = [];
    }
    acc[tripNumber].push(bid);
    return acc;
  }, {});

  const tripCount = Object.keys(groupedByTrip).length;

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`group relative flex items-center transition-all duration-200 ease-in-out ${
          isMobile
            ? 'flex-1 h-12 rounded-full bg-white shadow-lg border border-gray-200 px-4 gap-2 justify-between text-gray-700'
            : 'w-full h-12 rounded-xl gap-3 px-3 text-slate-600 hover:bg-slate-50 hover:text-slate-800 justify-start'
        }`}
        aria-label="Open shortlist"
      >
        {isMobile ? (
          <>
            <div className="flex items-center gap-2">
              <RiShoppingCartLine className="w-5 h-5" />
              <span className="text-sm font-medium">Shortlist</span>
            </div>
            <div className="flex items-center gap-1.5">
              {tripCount > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                  {tripCount} TRIP{tripCount !== 1 ? 'S' : ''}
                </span>
              )}
              <RiArrowRightSLine className="w-5 h-5 text-gray-400" />
            </div>
          </>
        ) : (
          <>
            <div className="relative flex-shrink-0">
              <RiShoppingCartLine className="w-5 h-5" strokeWidth={1.5} />
              {tripCount > 0 && (
                <span className="absolute min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-semibold rounded-full flex items-center justify-center ring-2 ring-white transition-all duration-300 -top-1 -right-2">
                  {tripCount > 9 ? '9+' : tripCount}
                </span>
              )}
            </div>
            <span className="opacity-100 md:opacity-0 md:group-hover/shortlist:opacity-100 md:group-hover/nav:opacity-100 transition-opacity duration-300 whitespace-nowrap text-sm font-medium overflow-hidden flex-1 text-left text-gray-500">
              Shortlist
            </span>
          </>
        )}
      </button>

      {/* Drawer */}
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerContent side="right" className="w-full !max-w-2xl overflow-y-auto !bg-gray-50" >
          <DrawerHeader>
              <div className="flex-1">
                <DrawerTitle className="!text-2xl font-semibold text-left">Shortlist</DrawerTitle>
              </div>
          </DrawerHeader>

          <div className="py-6 space-y-4">
            {tripCount === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <RiShoppingCartLine className="w-10 h-10 text-yellow-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No shortlisted bids</h3>
                <p className="text-sm text-gray-500 max-w-xs">
                  Star your favorite bids to add them to your shortlist for quick access.
                </p>
              </div>
            ) : (
              <div>
                {Object.entries(groupedByTrip).map(([tripNumber, bids]) => (
                  <div key={tripNumber} className="mb-3">
                    <h4 className="border border-gray-200 border-b-0 p-2 rounded-t-2xl text-lg text-gray-400 shadow-xl mb-[3px] bg-white">Trip: <span className="text-gray-500 !font-semibold">{tripNumber}</span></h4>
                    {bids.map((bid) => (
                      bid.bid_options
                        .filter(option => option.shortlisted === true)
                        .map((bidOption) => (
                          <div key={bidOption.bid_option_id} onClick={() => setIsOpen(false)}>
                            <ShortlistedCard
                              bid={bid}
                              bidOption={bidOption}
                              onViewBid={onViewBid}
                            />
                          </div>
                        ))
                    ))}

                    {/* Generate Quote Button Card */}
                    <div className="border border-gray-200 border-t-0 rounded-b-2xl p-4 bg-white hover:bg-gray-50 transition-colors shadow-xl">
                      <Button
                        variant="primary"
                        className="w-full rounded-full p-2"
                        onClick={() => {
                          setIsOpen(false);
                          // Clear shortlistAircraft before navigating to generate quote
                          clearShortlist();
                          setSelectedTripGroup(tripNumber, bids);
                          navigate(ROUTES.GENERATE_QUOTE.path);
                        }}
                      >
                        <RiFileList3Line className="w-5 h-5" />
                        Generate Quote
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default Shortlist;
